import { useState, useRef } from 'react';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { Label } from '../../ui/label';
import { challengesAPI } from '../../../lib/api';
import { toast } from 'sonner';
import { Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, XCircle, Loader2 } from 'lucide-react';

/**
 * Bulk-import startups from an Excel file with columns:
 * NOME · LOGO · FOTO · DESCRIZIONE · TAG LINKEDIN · TAG IG · Sito
 *
 * LOGO/FOTO cells may contain Google Drive share URLs (hyperlinks); they are
 * resolved and the images are uploaded to S3 by the backend.
 */
export const ImportStartupsDialog = ({ open, onOpenChange, challengeId, onImported }) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const reset = () => { setFile(null); setResult(null); setLoading(false); };

  const handleClose = (o) => {
    if (!o) {
      reset();
      onOpenChange(false);
    } else {
      onOpenChange(true);
    }
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0] || null;
    if (f && !/\.xlsx?$|\.xlsm$/i.test(f.name)) {
      toast.error('Carica un file Excel (.xlsx)');
      return;
    }
    setFile(f);
    setResult(null);
  };

  const handleImport = async () => {
    if (!file || !challengeId) return;
    setLoading(true);
    setResult(null);
    try {
      const { data } = await challengesAPI.importStartupsExcel(challengeId, file);
      setResult(data);
      const ok = data.imported_count;
      const skip = data.skipped_count;
      const fail = data.failed_count;
      if (fail > 0) toast.warning(`${ok} importate, ${skip} duplicate, ${fail} fallite`);
      else if (skip > 0) toast.success(`${ok} importate (${skip} duplicate)`);
      else toast.success(`${ok} startup importate`);
      if (ok > 0 && onImported) onImported();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Errore durante l\'import';
      toast.error(msg);
      setResult({ error: msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-card border-white/10 max-w-2xl" data-testid="import-startups-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet size={18} className="text-primary" />
            Importa Startup da Excel
          </DialogTitle>
          <DialogDescription className="text-xs">
            Colonne attese: <code>NOME</code>, <code>LOGO</code>, <code>FOTO</code>, <code>DESCRIZIONE</code>, <code>TAG LINKEDIN</code>, <code>TAG IG</code>, <code>Sito</code>.
            I link Google Drive nelle celle LOGO e FOTO vengono risolti automaticamente (i file devono essere condivisi "Chiunque con il link").
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* File picker */}
          <div className="space-y-2">
            <Label>File Excel (.xlsx, max 5MB)</Label>
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="file"
                accept=".xlsx,.xlsm"
                onChange={handleFileChange}
                className="hidden"
                data-testid="import-file-input"
              />
              <Button type="button" variant="outline" onClick={() => inputRef.current?.click()} data-testid="select-file-btn">
                <Upload size={14} className="mr-1" />
                Scegli file
              </Button>
              <span className="text-sm text-muted-foreground truncate">
                {file ? file.name : 'Nessun file selezionato'}
              </span>
            </div>
          </div>

          {/* Result panel */}
          {result && !result.error && (
            <div className="rounded-lg border border-white/10 bg-white/5 p-4 space-y-3 max-h-80 overflow-y-auto" data-testid="import-result">
              <div className="flex items-center gap-4 text-sm">
                <span className="flex items-center gap-1 text-emerald-400">
                  <CheckCircle2 size={14} /> {result.imported_count} importate
                </span>
                {result.skipped_count > 0 && (
                  <span className="flex items-center gap-1 text-amber-400">
                    <AlertTriangle size={14} /> {result.skipped_count} duplicate
                  </span>
                )}
                {result.failed_count > 0 && (
                  <span className="flex items-center gap-1 text-red-400">
                    <XCircle size={14} /> {result.failed_count} fallite
                  </span>
                )}
              </div>

              {result.imported?.some((i) => !i.logo_uploaded || !i.founder_photo_uploaded) && (
                <div className="text-xs text-amber-400/90 border-l-2 border-amber-400/40 pl-3">
                  Alcune immagini non sono state scaricate (file Drive non accessibili o link non immagine). Verifica i permessi di condivisione su Google Drive e ricarica manualmente le immagini mancanti.
                </div>
              )}

              {result.skipped?.length > 0 && (
                <div className="text-xs space-y-1">
                  <p className="font-semibold text-amber-400">Saltate (duplicati):</p>
                  <ul className="list-disc list-inside text-muted-foreground space-y-0.5">
                    {result.skipped.map((s, i) => (
                      <li key={i}>Riga {s.row}: <strong>{s.name}</strong></li>
                    ))}
                  </ul>
                </div>
              )}

              {result.failed?.length > 0 && (
                <div className="text-xs space-y-1">
                  <p className="font-semibold text-red-400">Fallite:</p>
                  <ul className="list-disc list-inside text-muted-foreground space-y-0.5">
                    {result.failed.map((s, i) => (
                      <li key={i}>Riga {s.row}: <strong>{s.name}</strong> — {s.error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => handleClose(false)} disabled={loading}>
              Chiudi
            </Button>
            <Button
              type="button"
              onClick={handleImport}
              disabled={!file || loading}
              data-testid="confirm-import-btn"
            >
              {loading ? <><Loader2 size={14} className="mr-1 animate-spin" /> Importazione…</> : <>Importa</>}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
